import React, { useEffect, useState } from "react";
import {
  Alert,
  Center,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Table,
  Text,
} from "@mantine/core";
import { getDeviceLogs } from "@utils/api-communication/RestApiCli";
import { deviceLogMessageFormatter } from "@model/DeviceLogs";
import { DEVICE_LOG_SEVERITY_ICON } from "@utils/deviceLogSeverityIcons";
import {
  DEVICE_LOG_CODES,
  DEVICE_LOG_SEVERITY_COLOR,
  DeviceLogEntry,
  deviceLogCode,
  deviceLogDetail,
  deviceLogSeverity,
  parseDeviceLogsResponse,
} from "@utils/deviceLogUtils";

interface Props {
  pid: string;
  limit?: number;
}

const PAGE_SIZE_OPTIONS = ["10", "25", "50", "100"];

const LOG_TYPE_OPTIONS = DEVICE_LOG_CODES.map((code) => ({
  value: code,
  label: deviceLogMessageFormatter(code),
}));

const DeviceLogs: React.FC<Props> = ({ pid, limit: initialLimit = 10 }) => {
  const [logs, setLogs] = useState<DeviceLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(initialLimit);
  const [code, setCode] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!pid) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    getDeviceLogs(
      pid,
      page,
      limit,
      (response) => {
        if (cancelled) return;
        const { logs: items, total: count } = parseDeviceLogsResponse(response);
        setLogs(items);
        setTotal(count);
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      },
      code ?? undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [pid, page, limit, code]);

  if (error) {
    return (
      <Text size="sm" c="red" py="xs">
        Failed to load device logs.
      </Text>
    );
  }

  // Without a total from the backend the page count is unknown, so keep paging
  // open as long as the page came back full.
  const totalPages =
    total !== null ? Math.max(1, Math.ceil(total / limit)) : logs.length < limit ? page : page + 1;

  return (
    <>
      <Group mb="xs" gap="xs" wrap="wrap">
        <Select
          size="xs"
          w={260}
          placeholder="All log types"
          data={LOG_TYPE_OPTIONS}
          value={code}
          onChange={(value) => {
            setCode(value);
            setPage(1);
          }}
          searchable
          clearable
          aria-label="Filter by log type"
        />
        {code && (
          <Text size="xs" c="dimmed">
            Filtering the whole history, not just this page
          </Text>
        )}
      </Group>

      <Paper withBorder radius="sm" p={0}>
        <Table verticalSpacing="xs" horizontalSpacing="sm" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={190}>Date</Table.Th>
              <Table.Th>Log</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={2}>
                  <Center py="md">
                    <Loader size="sm" />
                  </Center>
                </Table.Td>
              </Table.Tr>
            ) : logs.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={2}>
                  <Text c="dimmed" ta="center" py="sm" size="sm">
                    {code ? "No logs of this type for this device" : "No logs for this device"}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              logs.map((log, idx) => {
                const severity = deviceLogSeverity(log);
                const detail = deviceLogDetail(log);
                return (
                  <Table.Tr key={log._id || idx}>
                    <Table.Td>
                      <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                        {log.createdAt ? new Date(log.createdAt).toLocaleString() : "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Alert
                        color={DEVICE_LOG_SEVERITY_COLOR[severity]}
                        icon={DEVICE_LOG_SEVERITY_ICON[severity]}
                        variant="light"
                        p="xs"
                      >
                        <Text size="xs" fw={500} style={{ wordBreak: "break-word" }}>
                          {deviceLogMessageFormatter(deviceLogCode(log)) || "-"}
                        </Text>
                        {detail && (
                          <Text size="xs" c="dimmed" style={{ wordBreak: "break-word" }}>
                            {detail}
                          </Text>
                        )}
                      </Alert>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <Group justify="space-between" mt="xs" wrap="wrap" gap="xs">
        <Pagination
          value={page}
          onChange={setPage}
          total={totalPages}
          size="sm"
          withControls
          withEdges={total !== null}
          data-testid="device-logs-pagination"
        />
        <Group gap="xs" wrap="nowrap">
          {total !== null && (
            <Text size="xs" c="dimmed">
              {total} entries
            </Text>
          )}
          <Select
            size="xs"
            w={90}
            data={PAGE_SIZE_OPTIONS}
            value={String(limit)}
            onChange={(value) => {
              if (!value) return;
              setLimit(Number(value));
              setPage(1);
            }}
            allowDeselect={false}
            aria-label="Logs per page"
          />
        </Group>
      </Group>
    </>
  );
};

export default DeviceLogs;
export type { DeviceLogEntry };
